import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const NAMES = ['María', 'Laura', 'Sofía', 'Camila', 'Valentina', 'Martina', 'Lucía', 'Florencia', 'Paula', 'Daniela', 'Julieta', 'Agustina', 'Micaela', 'Carolina', 'Victoria', 'Rocío', 'Josefina', 'Delfina', 'Catalina', 'Milagros', 'Ana', 'Andrea', 'Natalia', 'Mariana', 'Romina', 'Bárbara', 'Guillermina', 'Sol', 'Macarena', 'Lourdes'];
const CITIES_BY_COUNTRY: Record<string, string[]> = {
  AR: ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Santiago del Estero', 'Corrientes', 'Posadas', 'Paraná', 'Bahía Blanca', 'San Salvador de Jujuy'],
  US: ['Miami', 'Houston', 'Los Angeles', 'New York', 'Chicago', 'San Antonio', 'Orlando', 'Dallas', 'Phoenix', 'San Diego', 'Denver', 'Atlanta', 'Las Vegas', 'Tampa', 'Austin', 'Charlotte', 'San Jose', 'Jacksonville'],
  ES: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Málaga', 'Bilbao', 'Zaragoza', 'Alicante', 'Murcia', 'Palma de Mallorca', 'Granada', 'Salamanca', 'Valladolid', 'Vigo', 'Gijón', 'Santander', 'San Sebastián', 'Córdoba'],
  MX: ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Cancún', 'Mérida', 'Querétaro', 'León', 'Tijuana', 'Toluca', 'Aguascalientes', 'San Luis Potosí', 'Chihuahua', 'Morelia', 'Oaxaca', 'Veracruz', 'Playa del Carmen', 'Hermosillo'],
  PE: ['Lima', 'Arequipa', 'Cusco', 'Trujillo', 'Chiclayo', 'Piura', 'Iquitos', 'Huancayo', 'Tacna', 'Cajamarca', 'Puno', 'Ayacucho', 'Ica', 'Chimbote', 'Juliaca', 'Sullana'],
  CO: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Pereira', 'Santa Marta', 'Manizales', 'Cúcuta', 'Ibagué', 'Villavicencio', 'Pasto', 'Armenia', 'Neiva', 'Montería'],
  EC: ['Quito', 'Guayaquil', 'Cuenca', 'Ambato', 'Manta', 'Loja', 'Riobamba', 'Machala', 'Portoviejo', 'Esmeraldas', 'Ibarra', 'Santo Domingo', 'Latacunga', 'Babahoyo']
};
const CENTRAL_AMERICA_COUNTRIES = ['GT', 'HN', 'SV', 'NI', 'CR', 'PA', 'BZ'];
const CENTRAL_AMERICA_CITIES = ['Ciudad de Guatemala', 'San José', 'Ciudad de Panamá', 'San Salvador', 'Tegucigalpa', 'Managua', 'Antigua Guatemala', 'San Pedro Sula', 'Quetzaltenango', 'Santa Ana', 'León', 'David', 'Heredia', 'Cartago', 'Colón', 'Escazú', 'Liberia', 'Cobán'];
const SOUTH_AMERICA_CITIES = ['Santiago', 'Montevideo', 'Asunción', 'La Paz', 'Santa Cruz', 'Valparaíso', 'Concepción', 'Cochabamba', 'Punta del Este', 'Viña del Mar', 'Sucre', 'Temuco', 'Antofagasta', 'Ciudad del Este'];
const SOUTH_AMERICA_COUNTRIES = ['CL', 'UY', 'PY', 'BO', 'VE', 'GY', 'SR', 'GF'];
const EUROPE_CITIES = ['Lisboa', 'Porto', 'Roma', 'Milán', 'París', 'Berlín', 'Ámsterdam', 'Bruselas', 'Viena', 'Zúrich', 'Múnich', 'Praga', 'Varsovia', 'Budapest', 'Estocolmo', 'Copenhague', 'Oslo', 'Helsinki'];
const EUROPE_COUNTRIES = ['PT', 'IT', 'FR', 'DE', 'NL', 'BE', 'AT', 'CH', 'CZ', 'PL', 'HU', 'SE', 'DK', 'NO', 'FI', 'IE', 'GB', 'RO', 'BG', 'HR', 'SK', 'SI', 'LT', 'LV', 'EE', 'GR'];
function getCitiesForCountry(countryCode: string): string[] {
  // Direct match
  if (CITIES_BY_COUNTRY[countryCode]) {
    return CITIES_BY_COUNTRY[countryCode];
  }
  // Central America
  if (CENTRAL_AMERICA_COUNTRIES.includes(countryCode)) {
    return CENTRAL_AMERICA_CITIES;
  }
  // Other South America
  if (SOUTH_AMERICA_COUNTRIES.includes(countryCode)) {
    return SOUTH_AMERICA_CITIES;
  }
  // Europe
  if (EUROPE_COUNTRIES.includes(countryCode)) {
    return EUROPE_CITIES;
  }
  // Brazil gets its own
  if (countryCode === 'BR') {
    return ['São Paulo', 'Rio de Janeiro', 'Brasília', 'Salvador', 'Fortaleza', 'Belo Horizonte', 'Curitiba', 'Recife', 'Porto Alegre', 'Manaus', 'Florianópolis', 'Goiânia', 'Belém', 'Campinas'];
  }
  // Default fallback: mix of Argentina + US cities
  return [...CITIES_BY_COUNTRY['AR'], ...CITIES_BY_COUNTRY['US']];
}
const DEFAULT_THUMB = 'https://cdn.magicpatterns.com/uploads/wkzGFuHWyP7z23EbiJvcUP/heylloyd-image-1772533088855.png';
interface ToastData {
  name: string;
  city: string;
  product: string;
  priceText: string;
  id: number;
}
function getRandomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function getRandomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickRandomPurchase(): {
  product: string;
  price: string;
} {
  const rand = Math.random();
  if (rand < 0.2) return {
    product: 'NutriPeques',
    price: '$12.99 USD'
  };
  if (rand < 0.6) return {
    product: 'NutriPeques + 1',
    price: '$18.98 USD'
  };
  return {
    product: 'NutriPeques + 2',
    price: '$24.97 USD'
  };
}
function useReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return prefersReduced;
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [geoReady, setGeoReady] = useState(false);
  const recentNamesRef = useRef<string[]>([]);
  const toastIdCounter = useRef(0);
  const citiesRef = useRef<string[]>([...CITIES_BY_COUNTRY['AR'], ...CITIES_BY_COUNTRY['US']]);
  const shouldReduceMotion = useReducedMotion();
  // Detect country on mount using ipapi.co (HTTPS, free)
  useEffect(() => {
    const cached = sessionStorage.getItem('nutripeques-country');
    if (cached) {
      citiesRef.current = getCitiesForCountry(cached);
      setGeoReady(true);
      return;
    }
    fetch('https://ipapi.co/json/').then(res => res.json()).then(data => {
      if (data?.country_code) {
        sessionStorage.setItem('nutripeques-country', data.country_code);
        citiesRef.current = getCitiesForCountry(data.country_code);
      }
    }).catch(() => {
      // Fallback: keep AR + US cities
    }).finally(() => {
      setGeoReady(true);
    });
  }, []);
  // Only start toast cycle after geo detection resolves
  useEffect(() => {
    if (!geoReady) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    const showNextToast = () => {
      // Pick name (avoid last 10)
      let availableNames = NAMES.filter(n => !recentNamesRef.current.includes(n));
      if (availableNames.length === 0) availableNames = NAMES;
      const selectedName = getRandomItem(availableNames);
      recentNamesRef.current = [selectedName, ...recentNamesRef.current].slice(0, 10);
      // Pick city from geo-detected list
      const selectedCity = getRandomItem(citiesRef.current);
      // Pick product + price
      const purchase = pickRandomPurchase();
      toastIdCounter.current += 1;
      setToast({
        name: selectedName,
        city: selectedCity,
        product: purchase.product,
        priceText: purchase.price,
        id: toastIdCounter.current
      });
      // Hide after 4s, then schedule next after 8–15s
      timeoutId = setTimeout(() => {
        setToast(null);
        timeoutId = setTimeout(showNextToast, getRandomInt(8000, 15000));
      }, 4000);
    };
    // Initial delay of 6s after geo is ready
    timeoutId = setTimeout(showNextToast, 6000);
    return () => clearTimeout(timeoutId);
  }, [geoReady]);
  const animDuration = shouldReduceMotion ? 0 : 0.4;
  return <AnimatePresence data-id="element-441">
      {toast && <motion.div key={toast.id} initial={{
      opacity: 0,
      scale: shouldReduceMotion ? 1 : 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: shouldReduceMotion ? 1 : 0.9
    }} transition={{
      duration: animDuration,
      ease: [0.16, 1, 0.3, 1]
    }} className="fixed bottom-6 left-0 right-0 mx-auto z-50 pointer-events-none px-4 w-full max-w-sm" data-id="element-442">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 p-3 flex items-center gap-3 w-full max-w-sm" data-id="element-443">
            {/* Thumbnail */}
            <div className="shrink-0 relative" data-id="element-444">
              <img src={DEFAULT_THUMB} alt="NutriPeques" className="w-12 h-12 rounded-xl object-cover shadow-sm border border-gray-50" data-id="element-445" />
              <div className="absolute -bottom-1 -right-1 bg-[#4CAF50] w-4 h-4 rounded-full border-2 border-white flex items-center justify-center" data-id="element-446">
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-447">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-448" />
                </svg>
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0" data-id="element-449">
              <p className="text-sm text-gray-800 leading-tight" data-id="element-450">
                <span className="font-bold" data-id="element-451">{toast.name}</span> de {toast.city}{' '}
                acaba de comprar
              </p>
              <p className="text-xs text-gray-500 mt-0.5 truncate" data-id="element-452">
                {toast.product}{' '}
                <span className="font-bold text-[#2E7D32]" data-id="element-453">
                  — {toast.priceText}
                </span>
              </p>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}